<template>
  <div
    :class="cardClasses"
    :data-deal-id="deal.id"
    :data-current-state="currentState"
  >
    <div class="dashboard-card__cover">
      <media-image
        v-if="officeOffer.cover"
        :uuid="officeOffer.cover.uuid"
        :alt="officeOffer.cover.description"
        :ratio="[ 294, 70 ]"
      />
      <img
        v-else
        :data-src="`${cdn_url}/images/1x1-grey.png`"
        :src="`${cdn_url}/images/1x1-grey.png`"
        alt="default"
      >
    </div>
    <div class="dashboard-card__content">
      <div class="dashboard-card__header dc-header">
        <div class="dc-header__interlocutor dimmable">
          <Profile
            :user="interlocutor"
            :user-firstname="interlocutorShortName"
            :user-fullname="interlocutorFullName"
          />
        </div>
        <dots-dropdown :dropdown-id="deal.id.toString()" class="dc-header__actions">
          <template #list-actions>
            <a :href="inboxMessageUrl" target="_blank">
              {{ trans('deal_dashboard.card.actions.to_inbox') }}
            </a>
            <a :href="officeOffer.uri" target="_blank" rel="noopener">
              {{ trans('deal_dashboard.card.actions.to_offer') }}
            </a>
            <a
              v-if="isVisitCancelable"
              :href="cancelVisitUrl"
              target="_blank"
            >
              {{ trans('deal_dashboard.card.actions.cancel_visit') }}
            </a>
            <a
              v-if="!deal.archived_by_buyer && !isAwaitingValidation"
              class="archive"
              @click="onArchiveClick"
            >
              {{ trans('deal_dashboard.card.actions.to_archive') }}
            </a>
          </template>
        </dots-dropdown>
      </div>
      <div class="ddc__address dimmable">
        {{ address }}
      </div>
      <div
        v-if="isCombination"
        class="ddc__combination ddc__specs"
      >
        <div class="ddc__description">
          {{ trans('deal_dashboard.card.offer.combination') }}
        </div>
        <div class="ddc__description">
          {{ currency(deal.amount) }} {{ trans('global.price_tax') }} /
          {{ trans('global.duration.month') }}
        </div>
      </div>
      <div
        v-if="isOpenSpace"
        class="ddc__open-space ddc__specs"
      >
        <div class="ddc__description">
          {{ officeOffer.office_type.name }} &bull;
          {{ transchoice('deal_dashboard.card.offer.seats_count', { count: teamSize }) }}
        </div>
        <div class="ddc__description">
          {{ currency(deal.amount / teamSize) }} {{ trans('global.price_tax') }} /
          {{ trans('deal_dashboard.card.offer.seat') }} /
          {{ trans('global.duration.month') }}
        </div>
      </div>
      <div
        v-if="isPrivateOrIndependent"
        class="ddc__private ddc__specs"
      >
        <div class="ddc__description">
          {{ officeOffer.office_type.name }}
        </div>
        <div class="ddc__description">
          {{ currency(deal.amount) }} {{ trans('global.price_tax') }} /
          {{ trans('global.duration.month') }}
        </div>
      </div>
      <div
        v-if="showMoveButton"
        class="ddc__move"
      >
        <extendable-button
          :id="`deal-card-${deal.id}`"
          :text="trans('deal_dashboard.card.actions.move')"
          :on-choose="onChoose"
          :list="moveButtonActions"
          :as-link="false"
        />
      </div>
    </div>
    <div
      v-if="isAwaitingValidation"
      class="dashboard-card__footer-item"
    >
      <span class="card-label card-label--awaiting tooltip">
        {{ trans('deal_dashboard.card.label.awaiting') }}
        <span class="card-label--icon" />
        <!-- eslint-disable vue/no-v-html -->
        <span
          class="tooltiptext"
          v-html="
            trans('deal_dashboard.card.label.awaiting.tooltip', {
              awaiting_match_validation_limit: awaitingMatchValidationLimit,
              company_name: $companyName,
            })
          "
        />
        <!-- eslint-enable vue/no-v-html -->
      </span>
    </div>
    <div
      v-if="isVisitShown"
      class="dashboard-card__visit dashboard-card__footer-item"
    >
      <card-visit :visit="deal.visited_date" />
    </div>
    <div
      v-if="deal.archived_by_buyer"
      class="dashboard-card__footer-item dashboard-card__footer-item--flex"
    >
      <span class="card-label card-label--archived dimmable">
        {{ trans('deal_dashboard.card.label.archived') }}
      </span>

      <span
        v-if="wasAwaitingValidation"
        class="card-label card-label--archived dimmable"
      >
        {{ trans('deal_dashboard.card.label.not_validated') }}
      </span>
    </div>
  </div>
</template>

<script>
import { oneLineAddress } from '@elements/address-utils';
import { DealStep } from '@enums/dealSteps';
import { storeToRefs } from 'pinia';
import ExtendableButton from '@components/ExtendableButton.vue';
import MediaImage from '@components/MediaImage.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import DotsDropdown from '@components-molecules/DotsDropdown.vue';
import Profile from '@components-molecules/Profile.vue';
import { currency } from '@elements/number-formatter';
import { isDateInFuture, setToEndOfDay } from '@tools/date';
import { eventBus } from '@tools/event-bus';
import { momentMixin } from '@tools/moment';
import { URL_DEAL_MESSAGING } from '@tools/routes/routes';
import { translationMixin } from '@elements/translation';
import { OfficeTypeEnum } from '../../offer/office-type-enum';
import { COLUMN__VISITED, EVENTS as BOARD_EVENTS } from '../constants';
import CardVisit from './CardVisit.vue';

export default {
  name: 'DashboardCard',
  components: { MediaImage, CardVisit, ExtendableButton, DotsDropdown, Profile },
  mixins: [ translationMixin, momentMixin ],
  props: {
    states: {
      type: Array,
      default: () => {},
    },
    deal: { type: Object, required: true },
    meta: {
      type: Object,
      default: () => {},
    },
    currentColumn: { type: String, required: true },
  },
  setup() {
    const { isDesktop } = storeToRefs(useScreensizeStore());

    return {
      isDesktop,
    };
  },
  data() {
    return {
      currentState: this.deal.state,
      cdn_url: window.CDN_URL,
      awaitingMatchValidationLimit: window.AWAITING_MATCH_VALIDATION_LIMIT ?? 20,
    };
  },
  computed: {
    cardClasses() {
      const classes = {
        'dashboard-card': true,
        'dashboard-card--draggable': this.isDraggable,
        'dashboard-card--not-draggable': !this.isDraggable,
        'dashboard-card--awaiting-validation': this.isAwaitingValidation,
      };
      classes[`dashboard-card--${this.currentColumn}`] = true;
      return classes;
    },
    isDraggable() {
      return this.isMovable && this.isDesktop;
    },
    isMovable() {
      return !this.isAwaitingValidation && !(this.isUnsigned && this.wasAwaitingValidation);
    },
    isAwaitingValidation() {
      return this.deal.state === DealStep.AWAITING_MATCH_VALIDATION;
    },
    isUnsigned() {
      return this.deal.state === DealStep.UNSIGNED;
    },
    isVisitCancelable() {
      return !this.deal.archived_by_buyer
        && this.isVisitShown
        && isDateInFuture(this.deal.visited_date.iso, setToEndOfDay());
    },
    wasAwaitingValidation() {
      return this.deal.previous_state === DealStep.AWAITING_MATCH_VALIDATION;
    },
    officeOffer() {
      return this.deal.first_office_offer;
    },
    dealOfficeOffers() {
      return this.deal.office_offers_collection || [];
    },
    interlocutor() {
      return this.deal.supervisor ?? this.deal.seller;
    },
    interlocutorShortName() {
      if (this.isAwaitingValidation || this.interlocutor.firstname !== '') {
        return this.interlocutor.firstname;
      }
      return this.interlocutor.lastname;
    },
    interlocutorFullName() {
      return this.isAwaitingValidation ? this.interlocutor.firstname : [ this.interlocutor.firstname, this.interlocutor.lastname ].filter((el) => el).join(' ');
    },
    isCombination() {
      return this.dealOfficeOffers.length > 1;
    },
    isOpenSpace() {
      return this.dealOfficeOffers.length === 1 && this.officeOffer.office_type.code === OfficeTypeEnum.OPEN_SPACE;
    },
    isPrivateOrIndependent() {
      return this.dealOfficeOffers.length === 1
        && [ OfficeTypeEnum.PRIVATE, OfficeTypeEnum.INDEPENDENT ].includes(this.officeOffer.office_type.code);
    },
    cancelVisitUrl() {
      return this.inboxMessageUrl.concat('?cancel-visit=1');
    },
    inboxMessageUrl() {
      return URL_DEAL_MESSAGING.replace('{id}', this.deal.id);
    },
    isVisitShown() {
      return this.currentColumn === COLUMN__VISITED && this.deal.visited_date !== null;
    },
    teamSize() {
      return this.dealOfficeOffers[0].team_size;
    },
    address() {
      return oneLineAddress(this.officeOffer.address.street, this.officeOffer.address.city, this.officeOffer.address.zip_code);
    },
    showMoveButton() {
      return this.isMovable && !this.isDesktop;
    },
    /**
     * Transform ['in_progress', 'state2', ..]
     * into [{state:'in_progress', trans:'En cours'}, {...}]
     */
    moveButtonActions() {
      return this.states
        .filter((state) => state !== this.currentColumn)
        .map((state) => ({
          state,
          trans: this.trans(`deal_dashboard.card.actions.move_to.${state}`),
        }));
    },
  },
  methods: {
    currency,
    onChoose(state) {
      eventBus.dispatchEvent(new CustomEvent(BOARD_EVENTS.CARD_MOVED, {
        detail: {
          dealId: this.deal.id,
          to: state,
          from: this.currentColumn,
          oldIndex: -1,
        },
      }));
    },
    onArchiveClick() {
      eventBus.dispatchEvent(new CustomEvent(BOARD_EVENTS.CARD_TO_ARCHIVE, {
        detail: {
          dealId: this.deal.id,
        },
      }));
    },
  },
};
</script>
